import Api from "./Api";
import { DateitUserMetadata } from "./data/Responses";
import { auth } from "firebaseui";
import { GoogleAuthProvider, EmailAuthProvider, signInWithCredential, getAuth, initializeAuth, indexedDBLocalPersistence, browserLocalPersistence, browserSessionPersistence, browserPopupRedirectResolver } from "firebase/auth";
import { datadogRum } from "@datadog/browser-rum";
import { getApp } from "firebase/app";
import { getAnalytics, logEvent } from "firebase/analytics";
import { nextTick } from "vue";
// @ts-ignore
import googleOneTap from "google-one-tap";

// Auth UI doesn't like to be in a vue property or observable object
// https://stackoverflow.com/a/70323161/1152375
export let authUi: auth.AuthUI;

// Good lord
// https://github.com/firebase/firebase-js-sdk/issues/4946#issuecomment-1636861479
export function provideAuth() {
    if (typeof document !== "undefined") {
        const isAuth = document.location.href.includes("auth");

        return initializeAuth(getApp(), {
            persistence: [indexedDBLocalPersistence, browserLocalPersistence, browserSessionPersistence],
            popupRedirectResolver: isAuth ? browserPopupRedirectResolver : undefined,
        });
    }
    return getAuth(getApp());
}

export default class AuthManager {
    metadata?: DateitUserMetadata;
    api: Api;

    displayName: string = "";
    displayPhoto?: string;
    authCallbacks: Array<() => void> = [];
    loginCallbacks: Array<() => void> = [];

    isLoggedIn: boolean = localStorage.userMetadata != null;
    accountRevision = 0;
    accountUid?: string;
    metadataRequest = 0;
    started = false;

    isAuthResolved: boolean = false;

    constructor(api: Api) {
        this.api = api;
        if (localStorage.getItem("userMetadata") !== null) {
            try {
                this.metadata = JSON.parse(localStorage.userMetadata);
                // Restore the cached profile immediately, but refresh SMS eligibility
                // from the server before showing an account-specific verification banner.
                if (this.metadata) this.metadata.smsVerificationRequired = undefined;
                this.setMetadata();
                this.isLoggedIn = this.metadata != null;
            } catch {
                this.metadata = undefined;
                this.isLoggedIn = false;
            }
        }
    }

    start() {
        if (this.started) return;
        this.started = true;
        authUi = new auth.AuthUI(provideAuth());
        provideAuth().onAuthStateChanged((user) => {
            void this.handleAccountChange(user?.uid);
        });

        // Hack to not show if user is logged in
        if (this.metadata == null) {
            const options = {
                client_id: "769971709933-fjt38sn28s6oqo0pd63ohs4b0ljfbtu5.apps.googleusercontent.com",
                auto_select: false,
                cancel_on_tap_outside: true,
                context: "signin",
                // use_fedcm_for_prompt: true,
            };

            // @ts-ignore
            googleOneTap(options, (response) => {
                const credential = GoogleAuthProvider.credential(response.credential);
                const auth = provideAuth();

                signInWithCredential(auth, credential)
                    .then((userCredential) => {})
                    .catch((error) => {
                        console.error(error);
                    });
            });
        }
    }

    async handleAccountChange(uid?: string) {
        // A restored Firebase session keeps its cached profile. Only a sign-out
        // or an actual account switch invalidates in-flight SMS/account work.
        if (!uid || (this.accountUid !== undefined && uid !== this.accountUid)) {
            this.clearAccount();
        }
        this.accountUid = uid;
        const revision = this.accountRevision;
        try {
            if (uid) {
                if (this.metadata == null) await this.api.firebaseLogin();
                if (revision !== this.accountRevision) return;
                this.isLoggedIn = true;
                this.loginCallAll();
                this.setAuthPending(false);
                void this.refreshMetadata();
                if (!window.location.pathname.toLowerCase().startsWith("/admin")) logEvent(getAnalytics(getApp()), "login_conversion");
            }
        } catch {
            // Avoid logging authentication payloads or account data.
            if (revision === this.accountRevision) this.isLoggedIn = false;
        } finally {
            if (revision === this.accountRevision) {
                this.isAuthResolved = true;
                this.isAuthCallAll();
            }
        }
    }

    clearAccount() {
        this.isAuthResolved = false;
        this.accountRevision++;
        this.metadataRequest++;
        this.accountUid = undefined;
        this.isLoggedIn = false;
        this.metadata = undefined;
        this.displayName = "";
        this.displayPhoto = undefined;
        localStorage.removeItem("userMetadata");
        datadogRum.clearUser();
    }

    async refreshMetadata(): Promise<boolean> {
        if (!this.isLoggedIn) return false;
        const revision = this.accountRevision;
        const request = ++this.metadataRequest;
        try {
            const metadata = await this.api.getUserMetadata();
            if (revision !== this.accountRevision || request !== this.metadataRequest || !this.isLoggedIn) return false;
            this.metadata = metadata;
            this.setMetadata();
            localStorage.userMetadata = JSON.stringify(metadata);
            return true;
        } catch {
            return false;
        }
    }

    setMetadata() {
        if (this.metadata == null) {
            return;
        }
        if (!window.location.pathname.toLowerCase().startsWith("/admin"))
            datadogRum.setUser({
                id: this.metadata.user.id,
                name: this.metadata.user.name,
                email: this.metadata.email,
                "session-id": Api.sessionId,
            });

        nextTick(() => {
            this.displayName = this.metadata?.user.name ?? "";
            this.displayPhoto = this.metadata?.user.photoUrl;
        });
    }

    signOut() {
        this.clearAccount();
        getAuth().signOut();
        //@ts-ignore
        window.location = "/";
    }

    async waitForAuthState() {
        if (this.isAuthResolved) {
            return;
        }
        await new Promise((resolve) => {
            this.authCallbacks.push(function () {
                resolve(true);
            });
        });
    }

    isAuthCallAll() {
        this.authCallbacks.forEach((x) => x());
    }

    loginCallAll() {
        this.loginCallbacks.forEach((x) => x());
    }

    onLogin(callback: () => void) {
        this.loginCallbacks.push(callback);
    }

    isAuthPending() {
        if (localStorage.getItem("authPending") !== null) {
            return JSON.parse(localStorage.authPending);
        }
        return false;
    }

    setAuthPending(val: boolean) {
        localStorage.authPending = val ? "true" : "false";
    }

    startFirebaseLogin(bindOn: string, onComplete: () => void) {
        authUi.start(bindOn, {
            callbacks: {
                signInSuccessWithAuthResult: (authResult: any, redirectUrl: string) => {
                    authResult;
                    redirectUrl;
                    this.setAuthPending(false);
                    onComplete();
                    return false;
                },
            },
            signInSuccessUrl: window.location.href,
            tosUrl: "/tos",
            privacyPolicyUrl: "/privacy",
            signInFlow: "popup",
            signInOptions: [
                {
                    provider: GoogleAuthProvider.PROVIDER_ID,
                    scopes: [],
                    customParameters: {
                        prompt: "select_account",
                    },
                },
                "apple.com",
                // { provider: PhoneAuthProvider.PROVIDER_ID },
                {
                    provider: EmailAuthProvider.PROVIDER_ID,
                },
            ],
        });
    }
}
